/**
 * Campaign picker + creation surface.
 *
 * One view file dispatches between four flavours based on the model's
 * `campaignsBoot` state:
 *
 *   - `loading`      — initial GET /v1/account in flight
 *   - `empty`        — zero campaigns; render the create-first form
 *   - `picker`       — campaigns.length >= 1; render the list + create
 *                       form (reachable directly at `/#/campaigns`)
 *   - `error`        — fetch failed; show retry copy
 *
 * The "1 campaign on initial boot" auto-select case is handled OUTSIDE
 * the view — the boot logic persists the id and reloads so the GM lands
 * directly in the entity shell. The picker route still works with 1
 * campaign because the user might WANT to see it (e.g. to create a
 * second one).
 *
 * Submitting the create form fires a single HTTP call
 * (POST /v1/campaigns). On success we persist the new campaign id to
 * localStorage and reload so the substrate, sync client, and viewer
 * cache rebuild cleanly against the new campaign.
 */
import { suggestFacilitationMode } from '@dl/shared';
import { button, pageHeader, sectionHeading, selectInput, textInput } from '@dl/ui';
import {
  branch,
  div,
  form,
  label,
  li,
  main,
  p,
  span,
  text,
  ul,
  type Mountable,
  type Renderable,
  type Send,
  type Signal,
} from '@llui/dom';

import {
  clearFormErrors,
  focusFirstError,
  setFieldError,
  setFormError,
  setSubmitting,
} from '../auth/forms.js';
import { t } from '../i18n.js';

import { monogram } from './accent.js';
import { createCampaign, type CampaignSummary } from './api.js';
import { getLastActive, setActiveCampaignId, touchLastActive } from './storage.js';

/**
 * #256 — the facilitation mode the create form pre-selects. A brand-new campaign
 * has exactly one member (the creator) and no other roles, so the shared
 * `suggestFacilitationMode` chokepoint proposes `solo`. Computed via the SAME
 * helper the auditor mismatch advisory uses, so the suggestion and the advisory
 * can never diverge. The user may override (the `gm` option stays in the list).
 */
const SUGGESTED_CREATE_MODE = suggestFacilitationMode({ memberCount: 1, hasGmRole: true });

/** Human-facing facilitation-mode label (mirrors the create-form options). */
function facilitationLabel(mode: CampaignSummary['facilitation_mode']): string {
  switch (mode) {
    case 'solo':
      return t('campaigns.facilitation.solo');
    case 'rotating':
      return t('campaigns.facilitation.rotating');
    case 'flat':
      return t('campaigns.facilitation.flat');
    default:
      return t('campaigns.facilitation.gm');
  }
}

export type CampaignsBoot =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'picker'; campaigns: readonly CampaignSummary[] }
  | { kind: 'error'; message: string };

/**
 * Persist + reload helper. Centralised so every entry point (picker
 * click, post-create) takes the identical path: write localStorage,
 * touch the last-active timestamp, drop the URL hash, and reload.
 *
 * The reload is intentional — it resets the module-scope substrate
 * caches (`replica`, `syncClient`, `cachedSessionViewer`) so the new
 * campaign's session/auth path runs cleanly from a blank slate. Same
 * pattern as sign-out / sign-in.
 */
export function enterCampaign(campaignId: string): void {
  setActiveCampaignId(campaignId);
  touchLastActive(campaignId);
  if (typeof window !== 'undefined') {
    // Full load at the app root so module-scope caches start clean.
    window.location.assign('/');
  }
}

async function onCreateSubmit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const formEl = event.currentTarget as HTMLFormElement;
  clearFormErrors(formEl);
  const data = new FormData(formEl);
  const name = String(data.get('name') ?? '').trim();
  const facilitationRaw = String(data.get('facilitation_mode') ?? 'gm');
  const facilitation: 'gm' | 'solo' | 'rotating' | 'flat' =
    facilitationRaw === 'solo' ? 'solo' : 'gm';

  if (name.length === 0) {
    setFieldError(formEl, 'campaign-name', t('campaigns.create.error_name_required'));
    focusFirstError(formEl);
    return;
  }

  setSubmitting(formEl, true);
  const result = await createCampaign({ name, facilitation_mode: facilitation });
  if (!result.ok) {
    setSubmitting(formEl, false);
    setFormError(formEl, result.error || t('campaigns.create.error_generic'));
    return;
  }

  enterCampaign(result.value.id);
}

const handleCreateSubmit = (event: Event): void => {
  void onCreateSubmit(event as SubmitEvent);
};

function formatLastActive(campaignId: string): string {
  const iso = getLastActive(campaignId);
  if (!iso) return t('campaigns.picker.never_active');
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return t('campaigns.picker.last_active', { at: fmt.format(d) });
  } catch {
    return t('campaigns.picker.last_active', { at: iso });
  }
}

function createFormRows(): Renderable {
  return [
    div({ class: 'auth-form-row' }, [
      label({ for: 'campaign-name' }, [text(t('campaigns.create.name_label'))]),
      textInput({
        type: 'text',
        placeholder: t('campaigns.create.name_placeholder'),
        testid: 'campaign-create-name',
        attrs: {
          id: 'campaign-name',
          name: 'name',
          required: true,
          autocomplete: 'off',
          'aria-describedby': 'campaign-name-error',
        },
      }),
      span(
        {
          id: 'campaign-name-error',
          'data-error-for': 'campaign-name',
          class: 'auth-field-error',
          role: 'alert',
        },
        [],
      ),
    ]),
    div({ class: 'auth-form-row' }, [
      label({ for: 'campaign-facilitation' }, [
        text(t('campaigns.create.facilitation_label')),
      ]),
      // #002 (llui-hud) — use the design-system `selectInput` instead of a raw
      // unstyled <select>. The form is read via `FormData` at submit, so the
      // control stays uncontrolled-after-mount (no model state) and `onChange`
      // is a no-op; `value` seeds the initial selection through selectInput's
      // onMount value reflector.
      //
      // #256 — creation-time auto-suggestion: a brand-new campaign has exactly
      // one member (the creator), so `suggestFacilitationMode` proposes `solo`.
      // It is a SUGGESTION — the `gm` option stays available (a user prepping
      // for future invitees keeps GM framing — 07-session-experience.md §2.1).
      selectInput({
        id: 'campaign-facilitation',
        name: 'facilitation_mode',
        testid: 'campaign-create-facilitation',
        ariaDescribedby: 'campaign-facilitation-help',
        size: 'full',
        value: SUGGESTED_CREATE_MODE === 'solo' ? 'solo' : 'gm',
        options: [
          { value: 'solo', label: t('campaigns.facilitation.solo') },
          { value: 'gm', label: t('campaigns.facilitation.gm') },
        ],
        // Uncontrolled: facilitation_mode is read from the form via FormData at
        // submit, so there is no model state to update here. Expression-bodied
        // (not an empty block) to satisfy no-empty-function.
        onChange: () => undefined,
      }),
      p(
        {
          id: 'campaign-facilitation-help',
          class: 'auth-field-help',
        },
        [text(t('campaigns.create.facilitation_help'))],
      ),
    ]),
    div(
      {
        'data-form-error': '',
        class: 'auth-form-error',
        role: 'alert',
      },
      [],
    ),
    button({
      variant: 'primary',
      type: 'submit',
      testid: 'campaign-create-submit',
      children: [text(t('campaigns.create.submit'))],
    }),
  ];
}

function viewCreateCard(heading: string, intro: string): Mountable {
  return div({ class: 'campaign-create-card', 'data-testid': 'campaign-create-card' }, [
    sectionHeading({ title: text(heading), level: 2 }),
    p({ class: 'campaign-create-intro' }, [text(intro)]),
    form(
      {
        class: 'campaign-create-form',
        'data-testid': 'campaign-create-form',
        novalidate: true,
        onSubmit: handleCreateSubmit,
      },
      createFormRows(),
    ),
  ]);
}

function viewLoading(): Mountable {
  return div(
    {
      class: 'campaigns-shell',
      'data-testid': 'campaigns-loading',
    },
    [
      div({ class: 'campaign-card', role: 'status', 'aria-live': 'polite' }, [
        p([text(t('campaigns.boot.loading'))]),
      ]),
    ],
  );
}

function viewError(message: string): Mountable {
  return div(
    {
      class: 'campaigns-shell',
      'data-testid': 'campaigns-error',
    },
    [
      div({ class: 'campaign-card' }, [
        pageHeader({ title: text(t('campaigns.title')) }),
        p({ class: 'campaign-error', role: 'alert' }, [
          text(message || t('campaigns.boot.error')),
        ]),
      ]),
    ],
  );
}

function viewEmpty(): Mountable {
  return div(
    {
      class: 'campaigns-shell',
      'data-testid': 'campaigns-empty',
    },
    [
      viewCreateCard(
        t('campaigns.create_first.heading'),
        t('campaigns.create_first.intro'),
      ),
    ],
  );
}

function viewPicker(campaigns: readonly CampaignSummary[]): Mountable {
  // Sort by last-active timestamp (newest first), falling back to
  // created_at. Read once at render time; ISO-8601 strings sort
  // lexicographically.
  const sorted = [...campaigns].sort((a, b) => {
    const la = getLastActive(a.id) ?? a.created_at;
    const lb = getLastActive(b.id) ?? b.created_at;
    return lb.localeCompare(la);
  });

  return div(
    {
      class: 'campaigns-shell',
      'data-testid': 'campaigns-picker',
    },
    [
      div({ class: 'campaign-card' }, [
        pageHeader({ title: text(t('campaigns.title')), intro: text(t('campaigns.intro')) }),
        ul(
          {
            class: 'campaign-list',
            'data-testid': 'campaign-list',
          },
          sorted.map((cmp) =>
            li(
              {
                class: 'campaign-list-item',
                'data-testid': 'campaign-list-item',
                'data-campaign-id': cmp.id,
              },
              [
                span(
                  {
                    class: 'campaign-list-mark',
                    'aria-hidden': 'true',
                  },
                  [text(monogram(cmp.name))],
                ),
                div({ class: 'campaign-list-body' }, [
                  div({ class: 'campaign-list-name' }, [text(cmp.name)]),
                  div({ class: 'campaign-list-meta' }, [
                    span({ class: 'campaign-list-mode' }, [
                      text(facilitationLabel(cmp.facilitation_mode)),
                    ]),
                    span({ class: 'campaign-list-dot', 'aria-hidden': 'true' }, [
                      text('·'),
                    ]),
                    span({}, [text(formatLastActive(cmp.id))]),
                  ]),
                ]),
                button({
                  variant: 'primary',
                  className: 'campaign-list-enter',
                  testid: 'campaign-enter',
                  ariaLabel: t('campaigns.switcher.current', { name: cmp.name }),
                  onClick: () => enterCampaign(cmp.id),
                  children: [text(t('campaigns.picker.enter'))],
                }),
              ],
            ),
          ),
        ),
        // Inline create-another form so the user can fan out without
        // navigating elsewhere. Same form, same submit handler.
        viewCreateCard(
          t('campaigns.picker.create_another'),
          t('campaigns.create_first.intro'),
        ),
      ]),
    ],
  );
}

/**
 * Public entry. The dispatcher renders this whenever:
 *   (a) we're at `/#/campaigns` (an explicit picker visit), OR
 *   (b) `campaignsBoot` is in a state that demands a picker (the
 *       initial boot path when no campaign is selected).
 *
 * `branch` keyed on `campaignsBoot.kind` mounts a fresh subtree per
 * boot state — the form input refs in the create card don't carry
 * over from the loading → empty transition. The per-arm `message` /
 * `campaigns` payloads are read one-shot (`.peek()`) at mount, matching
 * the prior `sub.sample(...)` semantics: each arm re-mounts when the
 * discriminant changes.
 */
export function viewCampaignsPicker<
  Model extends { campaignsBoot: CampaignsBoot },
  Msg,
>(state: Signal<Model>, _send: Send<Msg>): Mountable {
  // `state.at('campaignsBoot')` cannot resolve a precise path type through
  // the generic `Model` constraint (widens to `object`), so the discriminated
  // `branch` overload loses narrowing. `.map` keeps the `CampaignsBoot` type.
  const boot = state.map((s) => s.campaignsBoot);
  return main(
    {
      role: 'main',
      class: 'campaigns-shell-outer',
      'data-testid': 'campaigns-shell',
    },
    [
      branch(boot, (b) => b.kind, {
        loading: () => [viewLoading()],
        error: (v) => [viewError(v.peek().message)],
        empty: () => [viewEmpty()],
        picker: (v) => [viewPicker(v.peek().campaigns)],
      }),
    ],
  );
}
